import { cn } from "@/utils/cn";
import { useEffect, useRef } from "react";
import type { SearchProgress } from "../types";
import { FadeText } from "./Fade";

/** A grey block where text will land, pulsing gently; staggered by its delay. */
export const PulseBlock = ({ className, delay = 0 }: { className?: string; delay?: number }) => (
  <div aria-hidden className={cn("build-breathe h-2 rounded-[1px] bg-zinc-200", className)} style={{ animationDelay: `${delay}ms` }} />
);

/** Each step and how far along the bar a build at that step is at least. */
const STEPS = [
  { label: "Proposing columns", stages: ["plan"], floor: 0 },
  { label: "Searching", stages: ["search"], floor: 0.08 },
  { label: "Extracting", stages: ["screen", "read", "fill"], floor: 0.25 },
  { label: "Fixing rows", stages: ["check"], floor: 0.7 },
  { label: "Quality check", stages: ["final"], floor: 0.85 },
];
/** A build fills the bar in about this long. */
const FULL_MS = 120_000;

const stepOf = (stage: string | null) => Math.max(0, STEPS.findIndex((s) => stage && s.stages.includes(stage)));

const left = (ms: number) => {
  const s = Math.max(5, Math.round(ms / 5000) * 5);
  return s < 60 ? `about ${s} s left` : `about ${Math.floor(s / 60)} min${s % 60 ? ` ${s % 60} s` : ""} left`;
};

/** A bar that fills over the two minutes a build takes, never behind the step it is at; the step, the time left and
 * the latest message beside it. Ticks every frame by writing to the DOM so the table does not re-render. `paused`:
 * the columns wait for the user's approval, so the bar holds where searching starts and pulses. */
export const BuildStages = ({
  stage,
  message,
  startedAt,
  paused = false,
  progress = null,
}: {
  stage: string | null;
  message: string | null;
  startedAt: number | null;
  paused?: boolean;
  progress?: SearchProgress | null;
}) => {
  const at = paused ? 0 : stepOf(stage);
  const floor = paused ? STEPS[1].floor : STEPS[at].floor;
  const fill = useRef<HTMLDivElement>(null);
  const rest = useRef<HTMLSpanElement>(null);
  const shown = useRef(0);
  const since = useRef(startedAt ?? Date.now());
  if (startedAt) since.current = startedAt;
  useEffect(() => {
    if (paused) {
      shown.current = Math.max(shown.current, floor);
      if (fill.current) fill.current.style.width = `${(shown.current * 100).toFixed(2)}%`;
      if (rest.current) rest.current.textContent = "waiting for you";
      return;
    }
    let frame = 0;
    const tick = () => {
      const elapsed = Date.now() - since.current;
      shown.current = Math.min(0.98, Math.max(shown.current, floor, elapsed / FULL_MS));
      if (fill.current) fill.current.style.width = `${(shown.current * 100).toFixed(2)}%`;
      if (rest.current) rest.current.textContent = elapsed > FULL_MS ? "almost done" : left((1 - shown.current) * FULL_MS);
      frame = window.requestAnimationFrame(tick);
    };
    tick();
    return () => window.cancelAnimationFrame(frame);
  }, [floor, paused]);
  return (
    <div className="font-ui mt-3 max-w-[720px]" aria-label="Build progress">
      <div className="flex items-baseline justify-between gap-3 text-2xs uppercase tracking-wider">
        <span className="text-zinc-900" aria-current="step">
          {paused ? "Approve the columns to start" : STEPS[at].label}{" "}
          <span className="text-zinc-400">· step {at + 1} of {STEPS.length}</span>
        </span>
        <span ref={rest} className="tabular-nums normal-case tracking-normal text-zinc-500" />
      </div>
      <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-zinc-200" role="progressbar" aria-valuemin={0} aria-valuemax={100}>
        <div ref={fill} className={cn("h-full rounded-full bg-zinc-900", paused && "build-breathe")} style={{ width: 0 }} />
      </div>
      {progress ? (
        <SearchCounts progress={progress} />
      ) : (
        message && <p className="mt-1.5 max-w-[80ch] text-xs leading-snug text-zinc-500">{message}</p>
      )}
    </div>
  );
};

const count = (n: number) => n.toLocaleString("en-US");

/** A records search's live counts: the step under way, its searches and documents, the steps done, and while
 * it ranks, the most promising documents so far. */
const SearchCounts = ({ progress }: { progress: SearchProgress }) => {
  const done = progress.steps ?? [];
  return (
    <div className="mt-1.5 max-w-[80ch] text-xs leading-snug text-zinc-500" aria-live="polite">
      <p className="tabular-nums">
        {progress.final
          ? `${count(progress.found)} documents found and ranked · ${count(progress.reading)} read`
          : `${progress.step ? `Step ${progress.step} · ` : ""}${progress.searches_done} of ${progress.searches} searches · ${count(progress.found)} documents found · ${count(progress.ranked)} ranked`}
        {progress.lookup ? ` · lookup: ${count(progress.lookup)} match` : ""}
      </p>
      {done.length > 0 && (
        <p className="mt-0.5 tabular-nums text-zinc-400">
          {done.map((s) => `Step ${s.step}: ${s.searches} searches · ${count(s.new_records)} new · +${s.rows} rows`).join("  ·  ")}
        </p>
      )}
      {!progress.final && progress.top.length > 0 && (
        <div className="mt-1">
          <span className="text-2xs uppercase tracking-wider text-zinc-400">Most promising so far</span>
          <ul className="mt-0.5 space-y-0.5">
            {progress.top.slice(0, 3).map((title) => (
              <li key={title} className="text-zinc-600">
                <FadeText title={title}>{title}</FadeText>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const BAR_WIDTHS = ["w-3/4", "w-1/2", "w-5/6", "w-2/3", "w-2/5"];

/** Rows still to come: pulsing blocks where each cell's value will land, in a diagonal wave. */
export const SkeletonRows = ({ columns, count, faint = false }: { columns: number; count: number; faint?: boolean }) => (
  <>
    {Array.from({ length: count }, (_, r) => (
      <tr key={`skeleton-${r}`} aria-hidden className={cn("border-b border-zinc-100", faint && "opacity-50")}>
        <td />
        <td className="py-3 pr-4">
          <PulseBlock className="w-40 bg-zinc-100" delay={r * 150} />
          <PulseBlock className="mt-1.5 w-24 bg-zinc-100" delay={r * 150} />
        </td>
        {Array.from({ length: columns }, (_, c) => (
          <td key={c} className="py-3 pr-4">
            <PulseBlock className={cn("bg-zinc-100", BAR_WIDTHS[(r * 3 + c) % BAR_WIDTHS.length])} delay={(r + c) * 150} />
          </td>
        ))}
      </tr>
    ))}
  </>
);

/** A header row holding a hairline under the column names, a short segment gliding along it while the table is checked. */
export const CheckingLine = ({ span }: { span: number }) => (
  <tr aria-hidden>
    <th colSpan={span} className="relative h-0 p-0">
      <div className="absolute inset-x-0 -top-px h-px overflow-hidden">
        <div className="check-glide h-full w-1/5 bg-zinc-500" />
      </div>
    </th>
  </tr>
);

/** An image cell whose figure is still being looked at. */
export const FigureScanning = () => (
  <div
    className="build-breathe flex h-16 w-[120px] items-end rounded-[2px] bg-zinc-100"
    role="status"
    aria-label="Checking the figure"
  >
    <span className="font-ui px-1.5 pb-1 text-3xs text-zinc-400">Checking</span>
  </div>
);
